import apiClient from '@shared/api'

import type IVisitSessionResource from '@shared/api/visits/resources/anonymous/visit-session'
import type IVisitResource from '@shared/api/visits/resources/anonymous/visit'
import type IReasonListResource from '@shared/api/visits/resources/anonymous/reason-list'
import type IMessageResource from '@shared/api/visits/resources/anonymous/message'
import type IReporterResource from '@shared/api/visits/resources/anonymous/reporter'
import type IVisitReasonResource from '@shared/api/visits/resources/anonymous/visit-reason'
import type IVisitStatusResource from '@shared/api/visits/resources/anonymous/visit-status'
import type IVisitRating from '@shared/api/visits/resources/anonymous/rating'
import type IVisitToken from '@shared/api/visits/resources/anonymous/visit-token'

import type { LocationQueryValue, RouteParamValue } from 'vue-router'

export const createVisit = ({ visitId }: { visitId: RouteParamValue | RouteParamValue[] }): Promise<IVisitResource> => {
  return apiClient.post('/visits/qrCode', { visitId })
}

export const getVisitSession = ({
  qrCode,
  utmCampaignId,
}: {
  qrCode: RouteParamValue | RouteParamValue[]
  utmCampaignId?: LocationQueryValue | LocationQueryValue[] | null
}): Promise<IVisitSessionResource> => {
  return apiClient.get('/visits/sessions', { params: { qrCode, utmCampaignId } })
}

export const getVisitToken = ({ code }: { code: LocationQueryValue | LocationQueryValue[] }): Promise<IVisitToken> => {
  return apiClient.get(`/visits/${code}/tokens`)
}

export const getVisitGuestToken = ({
  code,
}: {
  code: LocationQueryValue | LocationQueryValue[]
}): Promise<IVisitToken> => {
  return apiClient.get('/visits/tokens', { params: { code } })
}

export const getVisitReasonList = (): Promise<IReasonListResource> => {
  return apiClient.get('/visits/reasons')
}

export const getVisitMessage = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IMessageResource> => {
  return apiClient.get(`/visits/${visitId}/messages/communities`)
}

export const getVisitReporter = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IReporterResource> => {
  return apiClient.get(`/visits/${visitId}/reporters`)
}

export const updateVisitReporter = ({
  visitId,
  data,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  data: IReporterResource
}): Promise<IReporterResource> => {
  return apiClient.put(`/visits/${visitId}/reporters`, data)
}

export const getVisitReason = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IVisitReasonResource> => {
  return apiClient.get(`/visits/${visitId}/reasons`)
}

export const updateVisitReason = ({
  visitId,
  data,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  data: IVisitReasonResource
}): Promise<IVisitReasonResource> => {
  return apiClient.put(`/visits/${visitId}/reasons`, data)
}

export const getVisitStatus = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IVisitStatusResource> => {
  return apiClient.get(`/visits/${visitId}/statuses`)
}

export const updateVisitStatus = ({
  visitId,
  visitStatus,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  visitStatus: string
}): Promise<IVisitStatusResource> => {
  return apiClient.put(`/visits/${visitId}/statuses`, { visitStatus })
}

export const sendVisitInvitationLink = ({
  visitId,
  email,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  email: string
}): Promise<unknown> => {
  return apiClient.put(`/visits/${visitId}/invitations`, { data: { email } })
}

export const getVisitRating = ({
  visitId,
}: {
  visitId: RouteParamValue | RouteParamValue[]
}): Promise<IVisitRating> => {
  return apiClient.get(`/visits/${visitId}/ratings`)
}

export const updateVisitRating = ({
  visitId,
  data,
}: {
  visitId: RouteParamValue | RouteParamValue[]
  data: IVisitRating
}): Promise<IVisitRating> => {
  return apiClient.put(`/visits/${visitId}/ratings`, data)
}
